import dayjs from 'dayjs';
import { to24Hour } from './describe-schedule';
import { AvailabilityRow } from 'server/types/cal-schema';

export type DateRange = {
  /**
   * When the block starts, in UTC.
   */
  start: Date;
  /**
   * When the block ends, in UTC.
   */
  end: Date;
};

export type TimeRange = {
  /**
   * 11:30AM
   */
  start: string;
  /**
   * 2:45PM
   */
  end: string;
};

/**
 * An allocated block of time, as well as the type of event associated
 * with it. This data allows us to determine if a event-type's maximum
 * daily limit has been met.
 */
type BookedRange = DateRange & { eventTypeId?: string };

export type AvailabilityOpts = {
  /**
   * The type of event, used to determine if the max per day
   * has been met.
   */
  eventTypeId: string;
  /**
   * The maximum number of these per day (<= 0 is unlimited).
   */
  maxPerDay: number;
  /**
   * The booked events / unavailable time ranges. The start / end
   * dates for these bookings *includes* the prefix / suffix buffer
   * times.
   */
  unavailable: BookedRange[];
  /**
   * The event duration in minutes.
   */
  duration: number;
  /**
   * The time zone of the attendee.
   */
  attendeeTimeZone: string;
  /**
   * The date whose *day* will be used to generate availability.
   */
  date: Date;
  /**
   * Availability windows, indexed by day of week (e.g. 0 = sunday, 6 = saturday)
   */
  availability: Pick<AvailabilityRow, 'timeslots' | 'timezone'>;
  /**
   * The number of minutes to reserve before the event starts.
   */
  prefixBufferTime: number;
  /**
   * The number of minutes to reserve after the event ends.
   */
  suffixBufferTime: number;
  /**
   * The minimum number of minutes into the future that a event should
   * be scheduled. This prevents someone from scheduling a event that
   * starts one minute from now, preventing the host from noticing it..
   */
  minNotice: number;
};

/**
 * Convert time into a standard, 24-hour format.
 */
function sanitizeTime(time: string) {
  time = time.toLowerCase();
  const [h, m] = time.split(/[^0-9]+/);
  return `${to24Hour(h, time.includes('a') ? 'am' : time.includes('p') ? 'pm' : '24')}:${
    m ? m : '00'
  }`;
}

/**
 * Converrt the time (e.g. "2:30pm") into a date.
 */
export function timeOfDay(date: Date | dayjs.Dayjs, time: string, timeZone: string) {
  return dayjs.tz(dayjs(date).format('YYYY-MM-DD') + ' ' + sanitizeTime(time), timeZone);
}

/**
 * Convert the basic availability time ranges into date time ranges.
 */
function* generateDateRanges(opts: AvailabilityOpts) {
  const targetDateStr = dayjs(opts.date).format('YYYY-MM-DD');
  for (const dayShift of [-1, 0, 1]) {
    const hostDay = dayjs.tz(targetDateStr, opts.availability.timezone).add(dayShift, 'days');
    const weekday = hostDay.day();
    const availableTimes = opts.availability.timeslots.filter((x) => x.days.includes(weekday));
    if (!availableTimes) {
      continue;
    }
    for (const range of availableTimes) {
      let curr = timeOfDay(hostDay, range.start, opts.availability.timezone);
      const end = timeOfDay(hostDay, range.end, opts.availability.timezone);
      while (true) {
        const rangeEnd = curr.add(opts.duration, 'minute');
        const range: DateRange = {
          start: curr.toDate(),
          end: rangeEnd.toDate(),
        };
        if (rangeEnd.isAfter(end)) {
          break;
        }
        yield range;
        curr = rangeEnd;
      }
    }
  }
}

/**
 * Create a function which filters out any date ranges which
 * overlap with the specified array. The array and the ranges
 * passed to the `isUnavailable` function are expected to be
 * sorted.
 */
function makeDateRangeFilter(unavailable: DateRange[]) {
  let i = 0;

  return function isUnavailable(range: DateRange) {
    while (i < unavailable.length) {
      const curr = unavailable[i];

      // The range is after our current unavailable block,
      // so we'll move forward.
      if (range.start >= curr.end) {
        ++i;
        continue;
      }

      // The range has no overlap
      if (range.end <= curr.start) {
        return false;
      }

      return true;
    }

    return false;
  };
}

/**
 * Adjust the unavailable blocks by the prefix / suffix buffers.
 */
function sanitizeUnavailableBlocks(opts: AvailabilityOpts) {
  const unavailable = opts.unavailable;
  if (opts.prefixBufferTime || opts.suffixBufferTime) {
    const prefix = -(opts.prefixBufferTime || 0);
    const suffix = opts.suffixBufferTime || 0;
    return unavailable.map((x) => ({
      ...x,
      start: dayjs(x.start).add(prefix, 'minute').toDate(),
      end: dayjs(x.end).add(suffix, 'minute').toDate(),
    }));
  }
  return unavailable.map((x) =>
    typeof x.start === 'string' ? { ...x, start: new Date(x.start), end: new Date(x.end) } : x,
  );
}

/**
 * Determine if the specified event type has met its daily max.
 */
function isAtDailyMax(opts: AvailabilityOpts) {
  if (opts.maxPerDay <= 0) {
    return;
  }
  const yyyymd = dayjs.tz(opts.date, opts.availability.timezone).format('YYYYMMDD');
  const numBooked = opts.unavailable.reduce((acc, x) => {
    if (x.eventTypeId !== opts.eventTypeId) {
      return acc;
    }
    if (dayjs(x.start).tz(opts.availability.timezone).format('YYYYMMDD') !== yyyymd) {
      return acc;
    }
    return acc + 1;
  }, 0);

  return numBooked >= opts.maxPerDay;
}

/**
 * Given the specified options, generate a list of available date ranges.
 */
export function* getAvailability(opts: AvailabilityOpts) {
  const minStart = dayjs().add(opts.minNotice, 'minute');
  const isUnavailable = makeDateRangeFilter(sanitizeUnavailableBlocks(opts));
  const targetDateStr = dayjs.tz(opts.date, opts.attendeeTimeZone).format('YYYY-MM-DD');

  if (isAtDailyMax(opts)) {
    return;
  }

  for (const range of generateDateRanges(opts)) {
    if (isUnavailable(range)) {
      continue;
    }
    if (minStart.isAfter(range.start)) {
      continue;
    }
    const startTz = dayjs.tz(range.start, opts.attendeeTimeZone);
    if (startTz.format('YYYY-MM-DD') === targetDateStr) {
      yield range;
    }
  }
}

/**
 * Sort bookings from oldest to newest.
 */
export function sortBookings(bookings: DateRange[]) {
  return bookings.sort((a, b) => {
    if (a.start < b.start) {
      return -1;
    }
    if (a.start > b.start) {
      return 1;
    }
    if (a.end < b.end) {
      return -1;
    }
    if (a.end > b.end) {
      return 1;
    }
    return 0;
  });
}
